import React from 'react'
import Radium from 'radium'

import { Col, Row, Grid } from "react-bootstrap"
import { markdown, PropAmount, valueOrUndefined } from '../common'
import propTypeSchema, { getComponentSchema } from 'react-json-schema-proptypes'
import DonationButtons from '../common/donation-buttons.jsx'
import ProjectSelector from '../common/project-selector.jsx'
import { Amount, Typography, Project } from '../types'
import ExtraFields from '../common/extra-fields'

const Tab = {
  Schema: {
    'type': 'object',
    'required': ['label', 'frequency', 'amounts'],
    'properties': {
      'label': { 'type': 'string' },
      'frequency': { 'type': 'string', 'enum': ['one-time', 'monthly'] },
      'amounts': { 'type': 'array', 'items': Amount.Schema },
      'customMessage': { 'type': 'string' },
    }
  },
  Style: {
    'type': 'object',
    'properties': {
      'activeTabColor': { 'type': 'string' },
      'inactiveTabColor': { 'type': 'string' },
      'underlineColor': { 'type': 'string' },
      'shadowColor': { 'type': 'string' },
      'style': Typography.Schema
    }
  }
}

const schema = {
  'type': 'object',
  'required': ['amountTabs'],
  'properties': {
    'type': { 'enum': ['beta'] },
    'buttons': DonationButtons.propSchema,
    'projectSelector': ProjectSelector.propSchema,
    'extraFields': ExtraFields.propSchema,
    'amountTabs': { 'type': 'array', 'items': Tab.Schema },
    'tabStyles': Tab.Style,
    'headerBackgroundColor': { 'type': 'string' },
    'headerLabel': {'type': 'string'},
    'monthlyGiftBanner': {'type': 'string'},
    'showMonthlyGivingBanner': {'type': 'boolean'},
    'header': Typography.Schema,
    'tabMessage': Typography.Schema,
    'mobileBreakPoint': { 'type': 'number' },
    'mobileMaxWidth': { 'type': 'number' },
    'showProjectSelector': { 'type': 'boolean' },
    'projectSelectorLabel': { 'type': 'string' },
    'astStyle': Typography.Schema,
    'checkStyle': Typography.Schema
  }
}

@Radium
export default class BetaGiftInfo extends React.PureComponent {
  static propSchema = schema

  static propTypes = propTypeSchema(schema, {
    'required': ['onAmountChanged', 'onProjectChanged', 'onValidChanged', 'onProjectMetadataChanged'],
    'properties': {
      'projects': { 'type': 'array', 'items': Project.Schema },
      'projectSelector': getComponentSchema(ProjectSelector),
      'extraFields': getComponentSchema(ExtraFields),
      'projectMetadata': { 'type': 'object' },
      'amount': Amount.Schema,
      'displayErrors': { 'type': 'boolean' },
      'selectedProject': Project.Schema,
      'onAmountChanged': { 'isFunction': true },
      'onProjectChanged': { 'isFunction': true },
      'onValidChanged': { 'isFunction': true },
      'onProjectMetadataChanged': { 'isFunction': true },
      'extraFieldsValues': { 'type': 'object' },
      'extraFieldsDependencies': { 'type': 'object' },
      'onChangeExtraFields': { 'isFunction': true },
      'onChangeExtraFieldsDependencies': { 'isFunction': true },
    }
  })

  static defaultProps = {
    tabStyles: {
      activeTabColor: '#FFFFFF',
      inactiveTabColor: '#F5E03C',
      underlineColor: '#F6E32A',
      shadowColor: '#A0932B'
    },
    headerLabel: '1. Donation Information',
    headerBackgroundColor: '#E61844',
    mobileBreakPoint: 992,
    mobileMaxWidth: 560,
    monthlyGiftBanner: 'donate-monthly.png',
    showMonthlyGivingBanner: false,
    projectSelectorLabel: 'Gift Designation',
    showTabMessage: true,
    showTabs: true
  }

  constructor(props) {
    super(props)
    this.state = {
      currentTab: this.props.amountTabs[0],
      metadataHasError: false,
      projectHasError: false,
      buttonsError: false,
      extraFieldsErrors: false
    }
  }

  componentDidMount() {
    if (this.props.extraFields) {
      const { extraFields } = this.props
      let required = {}
      let optionals = {}
      extraFields.forEach(function (extraField) {
        required = {
          ...required,
          [extraField.name]: extraField.dependencies
        }
        optionals = {
          ...optionals,
          [extraField.name]: extraField.optionalDependencies
        }
      })
      this.props.defaultDependencies(required, extraFields, optionals)
    }
  }

  handleButtonChange = (newAmount) => {
    if (newAmount) {
      newAmount.frequency = this.state.currentTab.frequency
    }
    this.props.onAmountChanged(newAmount)
  }

  handleTabChange = (newTab) => {
    this.setState({
      currentTab: newTab
    })
  }

  handleButtonValidChange = (valid) => {
    this.setState({
      buttonsError: !valid
    }, () => {
      this.props.onValidChanged(this.allValid())
    })
  }

  projectChanged = (newProject, hasError) => {
    this.props.onProjectChanged(newProject)
    this.props.onProjectMetadataChanged({})
    this.setState({
      projectHasError: hasError,
      metadataHasError: !!newProject.hasQuestion
    }, () => {
      this.props.onValidChanged(this.allValid())
    })
  }

  metadataChanged = (label, value, hasError) => {
    this.props.onProjectMetadataChanged({ [label]: value })
    this.setState({
      metadataHasError: hasError
    }, () => {
      this.props.onValidChanged(this.allValid())
    })
  }

  extraFieldChanged = (extraField, hasError, dependencies) => {
    this.setState({
      extraFieldsErrors: hasError
    }, () => {
      this.props.onChangeExtraFields(extraField, dependencies)
      this.props.onValidChanged(this.allValid())
    })
  }


  extraFieldDependenciesChanged = (extraField, field, hasError) => {
    this.props.onChangeExtraFieldsDependencies(extraField, field)
    this.setState({
      extraFieldsErrors: hasError
    }, () => {
      this.props.onValidChanged(this.allValid())
    })
  }

  handleExtraFieldValidChange = (hasError) => {
    this.setState({
      extraFieldsErrors: hasError
    }, () => {
      this.props.onValidChanged(this.allValid())
    })
  }

  allValid = () => {
    return !this.state.projectHasError && !this.state.metadataHasError && !this.state.buttonsError && !this.state.extraFieldsErrors
  }

  getTabs(styles) {
    let tabs = []
    let tabsCount = this.props.amountTabs.length
    this.props.amountTabs.forEach((tab, index) => {
      tabs.push(
        <Col key={tab.label} xs={tabsCount === 1 ? 12 : 6} className={'tabs' + (this.state.currentTab.label === tab.label ? ' activeTab' : '')}>
          <div onClick={() => this.handleTabChange(tab)} style={{ 'height': '42px' }}>
            <div
              className={(this.state.currentTab.label === tab.label ? ' active' : ' inactive') +
                (index === 0 && this.state.currentTab.label === tab.label ? ' ' :
                  index === 1 && this.state.currentTab.label === tab.label ? ' ' : index === 0 ? ' firstInactive' : ' secondInactive') + ' visible-xs-block visible-sm-block'}
              style={(this.state.currentTab.label === tab.label ? [styles.activeTab] : [styles.inactiveTab]).concat(
                (index === 0 && this.state.currentTab.label === tab.label ? [] :
                  index === 1 && this.state.currentTab.label === tab.label ? [] : index === 0 ? [styles.shadowColor] : [styles.shadowColor])
              )}
            >
              <span className={this.state.currentTab.label === tab.label ? 'underLine' : ''} style={([styles.underline]).concat([styles.tabTextStyle])}>{tab.label}</span>
              <div className={(index === 0 && this.state.currentTab.label === tab.label ? ' arrow-up arrowActive' :
                index === 1 && this.state.currentTab.label === tab.label ? ' arrow-up arrowActive' : index === 0 ?
                  ' arrow-down arrowInactive' : ' arrow-down arrowInactive')}
                style={(this.state.currentTab.label === tab.label) ? [styles.arrowActive] : []}
              />
            </div>
            <div
              className={(this.state.currentTab.label === tab.label ? ' active' : ' inactive') +
                (index === 0 && this.state.currentTab.label === tab.label ? ' firstActive' :
                  index === 1 && this.state.currentTab.label === tab.label ? ' secondActive' : index === 0 ? ' firstInactive' : ' secondInactive') + ' hidden-xs hidden-sm'}
              style={(this.state.currentTab.label === tab.label ? [styles.activeTab] : [styles.inactiveTab]).concat(
                (index === 0 && this.state.currentTab.label === tab.label ? [] :
                  index === 1 && this.state.currentTab.label === tab.label ? [] : index === 0 ? [styles.shadowColor] : [styles.shadowColor])
              )}
            />
            <span className={this.state.currentTab.label === tab.label ? 'underLine hidden-xs hidden-sm' : ' hidden-xs hidden-sm'}
                  style={([styles.underline]).concat([styles.tabTextStyle])}>{tab.label}</span>
            <div className={(index === 0 && this.state.currentTab.label === tab.label ? ' arrow-up arrowActive' :
              index === 1 && this.state.currentTab.label === tab.label ? ' arrow-up arrowActive' : index === 0 ?
                ' arrow-down arrowInactive' : ' arrow-down arrowInactive') + ' hidden-xs hidden-sm'}
              style={(this.state.currentTab.label === tab.label) ? [styles.arrowActive] : []}
            />
          </div>
        </Col>)
    })
    return tabs
  }

  render() {
    const styles = {
      activeTab: {
        backgroundColor: valueOrUndefined(this.props, 'tabStyles.activeTabColor')
      },
      monthlyBanner: {
        backgroundImage: `url(${this.props.assetLoader(this.props.monthlyGiftBanner)})`
      },
      inactiveTab: {
        backgroundColor: valueOrUndefined(this.props, 'tabStyles.inactiveTabColor')
      }, 
      underline: {
        borderColor: valueOrUndefined(this.props, 'tabStyles.underlineColor')
      },
      arrowActive: {
        color: valueOrUndefined(this.props, 'tabStyles.underlineColor')
      },
      shadowColor: {
        color: valueOrUndefined(this.props, 'tabStyles.shadowColor')
      },
      tabTextStyle: valueOrUndefined(this.props, 'tabStyles.style'),
      headerBackgroundColor: {},
      header: valueOrUndefined(this.props, 'header'),
      tabMessage: valueOrUndefined(this.props, 'tabMessage'),
      breakPointStyle: {}
    }
    if (this.props.mobileMaxWidth) {
      styles['breakPointStyle'][`@media (max-width: ${this.props.mobileBreakPoint - 1}px)`] = {
        maxWidth: this.props.mobileMaxWidth
      }
    }
    if (this.props.headerBackgroundColor) {
      styles['headerBackgroundColor'] = {
        backgroundColor: this.props.headerBackgroundColor
      }
    }

    return (
      <>
      {this.props.showMonthlyGivingBanner && (<div>
        <Row className='monthlyGiftBanner'>
          <Col xs={12}>
            <div className='monthly-gift' style={[styles.monthlyBanner]} />
          </Col>
        </Row>
      </div>)}
      <div className='beta container-fluid' style={styles.breakPointStyle}>
        <Row>
          <div className={'headers noMarginTop'} style={[styles.headerBackgroundColor, styles.header]}>
            {this.props.headerLabel}
          </div>
        </Row>
        { this.props.showTabs &&
        (<Row>
          {this.getTabs(styles)}
        </Row>
        )}

        { this.props.showTabMessage &&
        (<Row className={'tabMessage'}>
          <span className={'text'} style={[styles.tabMessage]}>{markdown(this.state.currentTab.customMessage)}</span>
        </Row>
        )}

        <DonationButtons maxRow={4} amount={this.props.amount} onAmountChanged={this.handleButtonChange} amounts={this.state.currentTab.amounts} specialAmounts={this.state.currentTab.specialAmounts} onValidChanged={this.handleButtonValidChange}
          displayErrors={this.props.displayErrors} customAmountFrequency={this.state.currentTab.frequency} {...this.props.buttons}
        />
        {this.props.projects && this.props.projects.length > 0 && this.props.showProjectSelector &&
          <div className={'rowPadding'}>
            <div className={'projectSelectorMessage'}>{this.props.projectSelectorLabel}</div>
            <ProjectSelector selectedProject={this.props.selectedProject} projects={this.props.projects}
              onChange={this.projectChanged} onChangeMetadata={this.metadataChanged}
              displayErrors={this.props.displayErrors} currentMetadata={this.props.projectMetadata}
              astStyle={this.props.astStyle}
              {...this.props.projectSelector} />
          </div>
        }

        {this.props.extraFields && this.props.extraFields.length > 0 &&
          <div className={'extraFieldRowPadding'}>
            <div className={'projectSelectorMessage'}> </div>
            <ExtraFields extraFieldsValues={this.props.extraFieldsValues}
              astStyle={this.props.astStyle}
              displayErrors={this.props.displayErrors}
              extraFieldsDependencies={this.props.extraFieldsDependencies}
              onChangeValues={this.extraFieldChanged}
              onChangeDependencies={this.extraFieldDependenciesChanged}
              extraFields={this.props.extraFields}
              handleValidChange={this.handleExtraFieldValidChange}
              checkStyle={this.props.checkStyle}
              {...this.props.extraFields} />
          </div>
        }
      </div>
      </>
    )
  }
}

